import React, { useCallback, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { programsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import BusinessCard from 'components/_common/panel/BusinessCard';
import ValidateTextArea from 'components/_common/forms/TextArea';
import { Pair } from 'types/Pair';
import { Dropdown } from 'primereact/dropdown';
import { DialogAction, DialogConfirm } from 'components/_new/Dialog';
import { Spinner } from 'components/_new/Spinner';
import { Application } from 'types/Application';
import { dateFormat } from 'utils/dateFormat';
import { dayjs } from 'utils/dayjs';
import { useGlobal } from 'contexts/globalContext';
import { Button } from 'components/_new/Button';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import PageMissing from 'components/PageMissing';
import { classNames } from 'primereact/utils';
import { prepareUserAreas } from 'utils/prepareUserAreas';
import { Message } from 'primereact/message';
import { scrollToElementById } from 'utils/scroll';
import { Box, BoxSection } from 'components/_new/Box';
import PairItem from 'components/userPanel/pairing/Pair';
import { getPairStatusInfo, getPairScoringText } from './utils';
import { EditApplicationDialog } from './EditApplicationDialog';
import { DeleteApplicationConfirmDialog } from './DeleteApplicationConfirmDialog';
import PairActivityTimestamps from '../_common/PairActivityTimestamps';
import TransferApplicationDialog from './TransferApplicationDialog';

const ExpandableRejectReason = ({ reason }: { reason: string }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);

	if (!reason) return null;

	const isLong = reason.length > 150;
	const maxLength = 150;

	if (!isLong) {
		return (
			<>
				<br />
				{t('programPanel.application.rejectReason', { reason })}
			</>
		);
	}

	return (
		<>
			<br />
			{t('programPanel.application.rejectReason', {
				reason: isExpanded ? reason : `${reason.slice(0, maxLength)}...`,
			})}
			<span
				onClick={() => setIsExpanded(!isExpanded)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setIsExpanded(!isExpanded);
					}
				}}
				className="ml-1 text-primary cursor-pointer hover:underline"
				style={{
					fontSize: 'inherit',
					textDecoration: 'underline',
					display: 'inline',
					userSelect: 'none',
				}}
				aria-label={isExpanded ? 'Zwiń tekst' : 'Rozwiń pełny tekst'}
			>
				{isExpanded ? t('actions.showLess') : t('actions.expand')}
			</span>
		</>
	);
};

const useApplicationActions = (
	application: Application | undefined,
	program: any,
	handlers: any,
	loadingStates: any
) => {
	const { t } = useTranslation();

	return useMemo(() => {
		if (!application) return [];

		const actions = [];
		const { sending, restoringApplication, finalDeleteApplicationLoading, sendEditSuccess } = loadingStates;
		const {
			handleAccept,
			setRejectConfirmDialogOpen,
			setEditApplicationDialogOpen,
			handleCancelDeleting,
			setRestoreConfirmDialogOpen,
			setRemoveApplicationDialogOpen,
			setTransferDialogVisible,
		} = handlers;

		if (application.approved === 'not accepted') {
			actions.push(
				{
					id: 'accept-with-notification',
					label: t('actions.acceptWithNotification'),
					onClick: () => handleAccept(true),
					loading: sending,
					tooltip: t('programPanel.application.acceptWithNotificationTooltip'),
					variant: 'primary',
				},
				{
					id: 'accept-without-notification',
					label: t('actions.accept'),
					onClick: () => handleAccept(false),
					variant: 'primary-text',
					disabled: sending,
					tooltip: t('programPanel.application.acceptWithoutNotificationTooltip'),
				},
				{
					id: 'reject',
					label: t('actions.reject'),
					onClick: () => setRejectConfirmDialogOpen(true),
					variant: 'primary-text',
					disabled: sending,
				}
			);
		}

		if (application.approved === 'accepted') {
			actions.push({
				id: 'withdraw',
				label: t('programPanel.application.withdrawApplication'),
				onClick: () => setRejectConfirmDialogOpen(true),
				variant: 'primary-text',
				loading: sending,
			});
		}
		actions.push({
			id: 'transfer',
			label: t('programPanel.application.transferApplication'),
			onClick: () => setTransferDialogVisible(true),
			variant: 'primary-text',
			disabled: sending,
			tooltip: t('programPanel.application.transferApplicationTooltip'),
		});
		actions.push({
			id: 'edit',
			label: t('programPanel.application.editApplicationData'),
			onClick: () => setEditApplicationDialogOpen(true),
			variant: 'primary-text',
			loading: sending,
			disabled: !application,
		});

		if (application.deletedAt) {
			actions.push({
				id: 'cancel-delete',
				label: t('programPanel.pairing.removeApplication.cancelAction', {
					datetime: dateFormat(dayjs(application.deletedAt).toDate(), 'date'),
				}),
				onClick: handleCancelDeleting,
				variant: 'primary-text',
				loading: sending,
			});
		}

		if (application.approved === 'rejected') {
			actions.push({
				id: 'restore',
				label: t('programPanel.application.restoreApplication'),
				onClick: () => setRestoreConfirmDialogOpen(true),
				variant: 'primary-text',
				loading: restoringApplication,
			});
		}

		if (!application.deletedAt) {
			actions.push({
				id: 'remove',
				label: t('programPanel.pairing.removeApplication.removeAction'),
				onClick: () => setRemoveApplicationDialogOpen(true),
				variant: 'primary-text',
				loading: finalDeleteApplicationLoading,
			});
		}

		return actions;
	}, [application, loadingStates, handlers, t]);
};

const ActionButton = ({ action }: { action: any }) => (
	<Button
		onClick={action.onClick}
		label={action.label}
		variant={action.variant}
		loading={action.loading}
		disabled={action.disabled}
		tooltip={action.tooltip}
	/>
);

const ProgramPairingDetails = () => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();
	const queryClient = useQueryClient();
	const { data: program, isSuccess } = useProgramPanel();
	const params = useParams<{ applicationId: string }>();
	const applicationId = Number(params.applicationId);

	// States
	const [manualModel, setManualMode] = useState(false);
	const [transferDialogVisible, setTransferDialogVisible] = useState(false);
	const [pairs, setPairs] = useState<Pair[]>([]);
	const [rejectConfirmDialogOpen, setRejectConfirmDialogOpen] = useState(false);
	const [rejectWithNotification, setRejectWithNotification] = useState(true);
	const [editApplicationDialogOpen, setEditApplicationDialogOpen] = useState(false);
	const [currentPair, setCurrentPair] = useState<null | number>(null);
	const [acceptPairDialogOpen, setAcceptPairDialogOpen] = useState(false);
	const [removePairDialogOpen, setRemovePairDialogOpen] = useState(false);
	const [restoreConfirmDialogOpen, setRestoreConfirmDialogOpen] = useState(false);
	const [removeApplicationDialogOpen, setRemoveApplicationDialogOpen] = useState(false);
	const [applicationToAdd, setApplicationToAdd] = useState<null | number>(null);
	const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

	const {
		data: application,
		isLoading: applicationLoading,
		isError: applicationError,
		refetch: refetchApplication,
	} = useQuery(
		['getProgramApplication', applicationId],
		() => programsApi.getProgramAdminApplication({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && applicationId),
		}
	);

	const { data: applicationLastChange, isLoading: isLoadingLastChange } = useQuery(
		['getApplicationLastChange', { programId: program?.id, applicationId }],
		() => programsApi.getApplicationLastChange({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isSuccess && program?.id && applicationId),
		}
	);

	const { data: applicationChangelogHistory, isLoading: isLoadingHistory } = useQuery(
		['getApplicationChangelogHistory', { programId: program?.id, applicationId }],
		() => programsApi.getApplicationChangelogHistory({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isHistoryExpanded && isSuccess && program?.id && applicationId),
		}
	);

	const {
		isLoading: pairsLoading,
		refetch: refetchPairs,
		isRefetching,
	} = useQuery(
		['getProgramApplicationPairs', applicationId],
		() => programsApi.getProgramAdminApplicationPairing({ programId: Number(program?.id), applicationId }),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && applicationId) && !manualModel,
			onSuccess: (data) => {
				if (data && data.length > 0) {
					setPairs(data);
				}
			},
		}
	);

	const { data: propositionsData, refetch: refetchPropositions } = useQuery(
		['getAvaiablePairsForApplication', program?.id],
		() =>
			programsApi.getAvailablePairsForApplication({
				programId: Number(program?.id),
				applicationId: Number(application?.id),
			}),
		{
			enabled: Boolean(isSuccess && Number(program?.id) && application?.id),
		}
	);

	// Mutations
	const {
		mutateAsync,
		isLoading: sending,
		isSuccess: sendEditSuccess,
	} = useMutation(programsApi.editProgramApplication, {
		onSuccess: () => {
			refetchApplication();
			queryClient.invalidateQueries(['getApplicationLastChange', { programId: program?.id, applicationId }]);
			queryClient.invalidateQueries([
				'getApplicationChangelogHistory',
				{ programId: program?.id, applicationId },
			]);
			setRejectConfirmDialogOpen(false);
		},
	});

	const { mutateAsync: acceptPair, isLoading: acceptPairLoading } = useMutation(programsApi.acceptProgramPair, {
		onSuccess: () => {
			setManualMode(true);
			setAcceptPairDialogOpen(false);
			setPairs((prev) =>
				prev.map((item) => {
					if (item.id === currentPair) {
						return {
							...item,
							acceptedByMentee: new Date().toISOString(),
							acceptedByMentor: new Date().toISOString(),
							entryMeeting: new Date().toISOString(),
							status: 'accepted',
						};
					}
					return item;
				})
			);
			setCurrentPair(null);
			refetchApplication();
			toastRef.current?.show({
				severity: 'success',
				life: 5000,
				summary: t('misc.success'),
				detail: t('programPanel.application.pairPropositions.acceptedSuccessInfo'),
			});
		},
	});

	const { mutateAsync: deletePair, isLoading: deletePairLoading } = useMutation(programsApi.deleteProgramPair, {
		onSuccess: () => {
			setManualMode(true);
			setRejectConfirmDialogOpen(false);
			setPairs((prev) => prev.filter((item) => item.id !== currentPair));
			setCurrentPair(null);
			refetchApplication();
			toastRef.current?.show({
				severity: 'success',
				life: 5000,
				summary: t('misc.success'),
				detail: t('programPanel.application.statusInfo.removed'),
			});
		},
	});

	const { mutate: addPair, isLoading: isAddingPair } = useMutation(programsApi.addProgramPair, {
		onSuccess: (newPair) => {
			refetchPropositions();
			if (newPair) {
				const preparedPair = {
					...newPair,
					programMembership:
						application?.applicationRole === 'mentor' ? newPair.menteeMembership : newPair.mentorMembership,
					application:
						application?.applicationRole === 'mentor'
							? newPair.menteeApplication
							: newPair.mentorApplication,
				};

				setPairs((prev) =>
					[...prev, preparedPair as unknown as Pair].sort((a, b) => {
						if (a.status === 'accepted' && b.status !== 'accepted') return -1;
						if (a.status === 'open' && b.status === 'accepted') return 1;
						if (a.createdAt > b.createdAt) return -1;
						if (a.createdAt < b.createdAt) return 1;
						return 0;
					})
				);

				setTimeout(() => {
					scrollToElementById(`pair-${newPair.id}`);
				}, 100);
			}
			setApplicationToAdd(null);
		},
	});

	const { mutate: deleteApplication, isLoading: deleteApplicationLoading } = useMutation(
		programsApi.deleteProgramApplications,
		{
			onSuccess: (data, variables) => {
				refetchApplication();
				toastRef.current?.show({
					severity: 'info',
					life: 5000,
					summary: t('misc.info'),
					detail: variables.quickDelete
						? t('programPanel.pairing.removeApplication.infoQuick')
						: t('programPanel.pairing.removeApplication.info'),
				});
			},
		}
	);

	const { mutate: cancelDeleteApplication, isLoading: cancelDeleteApplicationLoading } = useMutation(
		programsApi.cancelDeleteProgramApplication,
		{
			onSuccess: () => {
				refetchApplication();
			},
		}
	);

	const { mutateAsync: restoreApplication, isLoading: restoringApplication } = useMutation(
		programsApi.restoreProgramApplication,
		{
			onSuccess: () => {
				refetchApplication();
				queryClient.invalidateQueries(['getApplicationLastChange', { programId: program?.id, applicationId }]);
				queryClient.invalidateQueries([
					'getApplicationChangelogHistory',
					{ programId: program?.id, applicationId },
				]);
				toastRef.current?.show({
					severity: 'success',
					life: 5000,
					summary: t('misc.success'),
					detail: t('programPanel.application.statusInfo.restored'),
				});
			},
		}
	);

	// Handlers
	const handleAccept = useCallback(
		(withNotification = true) => {
			if (program && application) {
				mutateAsync({
					programId: program?.id,
					applicationId: application?.id,
					status: 'accepted',
					withNotification,
				});
			}
		},
		[program, application, mutateAsync]
	);

	const handleReject = useCallback(
		(reason: string, withNotification = true) => {
			if (program && application) {
				return mutateAsync({
					programId: program?.id,
					applicationId: application?.id,
					status: 'rejected',
					reason,
					withNotification,
				});
			}
			return undefined;
		},
		[program, application, mutateAsync]
	);

	const handleAcceptPair = useCallback(() => {
		if (currentPair && program) {
			return acceptPair({ programId: program.id, pairId: currentPair });
		}
		return undefined;
	}, [currentPair, program, acceptPair]);

	const handleRemovePair = useCallback(() => {
		if (currentPair && program) {
			return deletePair({ programId: program.id, pairId: currentPair });
		}
		return undefined;
	}, [currentPair, program, deletePair]);

	const handleCancelDeleting = useCallback(() => {
		cancelDeleteApplication({ programId: Number(program?.id), applicationId: Number(application?.id) });
	}, [program, application, cancelDeleteApplication]);

	const handleRestoreApplication = useCallback(() => {
		if (program && application) {
			restoreApplication({
				programId: program?.id,
				applicationId: application?.id,
			});
		}
	}, [program, application, restoreApplication]);

	const handleAddPair = useCallback(() => {
		if (program && application && applicationToAdd) {
			addPair({ programId: program?.id, applicationId: application?.id, pairApplicationId: applicationToAdd });
		}
	}, [program, application, applicationToAdd, addPair]);

	// Forms
	const { control, trigger, getValues } = useForm<{ reason: string }>();
	const { control: deleteApplicationControl, handleSubmit: deleteApplicationHandleSubmit } = useForm({
		defaultValues: { withUser: false, sendMail: true, quickDelete: false },
	});

	const handleFormSubmit = deleteApplicationHandleSubmit(({ withUser, sendMail, quickDelete }) => {
		return deleteApplication({
			programId: Number(program?.id),
			applicationIds: [Number(application?.id)],
			withUser,
			sendMail,
			quickDelete,
		});
	});

	// Helper functions for changelog
	const getEventTypeLabel = (eventType: string): string => {
		const labels: Record<string, string> = {
			application_created: t('programPanel.application.changelog.applicationCreated'),
			application_accepted_with_notification: t('programPanel.application.changelog.acceptedWithNotification'),
			application_accepted_without_notification: t(
				'programPanel.application.changelog.acceptedWithoutNotification'
			),
			application_rejected: t('programPanel.application.changelog.applicationRejected'),
			application_restored: t('programPanel.application.changelog.applicationRestored'),
			application_withdrawn: t('programPanel.application.changelog.applicationWithdrawn'),
		};
		return labels[eventType] || eventType;
	};

	const getApplicationStatusLabel = (status: string): string => {
		const labels: Record<string, string> = {
			'not accepted': t('programPanel.application.statusOptions.notAccepted'),
			accepted: t('programPanel.application.statusOptions.accepted'),
			rejected: t('programPanel.application.statusOptions.rejected'),
		};
		return labels[status] || status;
	};

	const renderChangeItem = (change: any, isLast = false) => {
		const adminName = change.admin ? `${change.admin.firstName} ${change.admin.lastName}` : t('misc.unknown');

		return (
			<div
				key={change.id}
				className={classNames('flex align-items-start gap-3 py-2', {
					'border-bottom-1 border-gray-200': !isLast,
				})}
			>
				<div className="flex flex-column align-items-center" style={{ width: '24px' }}>
					<div className="bg-primary border-circle" style={{ width: '8px', height: '8px' }} />
					{!isLast && <div className="bg-gray-300" style={{ width: '2px', flex: 1, marginTop: '4px' }} />}
				</div>

				<div className="flex-1">
					<div className="flex justify-content-between align-items-start mb-1">
						<span className="font-semibold text-900">{getEventTypeLabel(change.eventType)}</span>
						<span className="text-sm text-500">{dateFormat(change.createdAt, 'full')}</span>
					</div>

					{change.previousStatus && change.newStatus && (
						<div className="text-sm text-600 mb-1">
							{getApplicationStatusLabel(change.previousStatus)} →{' '}
							{getApplicationStatusLabel(change.newStatus)}
						</div>
					)}

					<div className="text-sm text-500 flex align-items-center gap-1">
						<i className="pi pi-user text-xs" />
						<span>{adminName}</span>
					</div>
				</div>
			</div>
		);
	};

	const renderChangelogSection = () => {
		if (isLoadingLastChange) {
			return <span className="text-sm text-500 ml-2">{t('misc.loading')}...</span>;
		}

		if (!applicationLastChange) {
			return null;
		}

		const hasMoreChanges = applicationChangelogHistory && applicationChangelogHistory.length > 1;

		return (
			<div className="mt-3 p-3 bg-gray-50 border-round">
				<div className="flex align-items-center justify-content-between">
					<div className="flex align-items-center gap-2 flex-1">
						<i className="pi pi-info-circle text-primary" />
						<div className="text-sm">
							<span className="font-semibold">{t('programPanel.application.lastChange')}:</span>{' '}
							{getEventTypeLabel(applicationLastChange.eventType)}
							{applicationLastChange.previousStatus && applicationLastChange.newStatus && (
								<>
									{' '}
									<span className="text-600">
										({getApplicationStatusLabel(applicationLastChange.previousStatus)} →{' '}
										{getApplicationStatusLabel(applicationLastChange.newStatus)})
									</span>
								</>
							)}
							{' · '}
							<span className="font-medium">
								{applicationLastChange.admin.firstName} {applicationLastChange.admin.lastName}
							</span>
							{' · '}
							<span className="text-500">{dateFormat(applicationLastChange.createdAt, 'full')}</span>
						</div>
					</div>

					{hasMoreChanges && (
						<Button
							variant="white-text"
							variantSize="sm"
							icon={isHistoryExpanded ? 'chevron-up' : 'chevron-down'}
							iconSet="pi"
							onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
							label={
								isHistoryExpanded
									? t('programPanel.application.hideHistory')
									: t('programPanel.application.showHistory', {
											count: (applicationChangelogHistory?.length || 1) - 1,
									  })
							}
						/>
					)}
				</div>

				{isHistoryExpanded && hasMoreChanges && (
					<div className="mt-3 pt-3 border-top-1 border-gray-200">
						{isLoadingHistory ? (
							<div className="flex justify-content-center p-3">
								<Spinner />
							</div>
						) : (
							<>
								<div className="text-sm font-semibold text-600 mb-3">
									{t('programPanel.application.previousChanges')}:
								</div>
								<div className="flex flex-column">
									{applicationChangelogHistory
										.slice(1)
										.map((change, index, array) =>
											renderChangeItem(change, index === array.length - 1)
										)}
								</div>
							</>
						)}
					</div>
				)}
			</div>
		);
	};

	// Computed values
	const systemAnswers = application?.systemAnswers;
	const userAreas = prepareUserAreas(systemAnswers?.areas || []);
	const finalDeleteApplicationLoading = cancelDeleteApplicationLoading || deleteApplicationLoading;
	const propositions = propositionsData || [];
	const currentPairsApplicationIdentifiers = (pairs || []).reduce<number[]>(
		(curr, pair) => [...curr, pair.menteeApplicationId, pair.mentorApplicationId],
		[]
	);
	const currentApplicationPairLimitReached = Boolean(application?.limitStats?.pairLimitReached);

	const sortedPairs = useMemo(() => {
		return [...(pairs || [])].sort((a, b) => {
			const aIsAccepted = a.acceptedByMentee !== null || a.acceptedByMentor !== null;
			const bIsAccepted = b.acceptedByMentee !== null || b.acceptedByMentor !== null;

			if (aIsAccepted && !bIsAccepted) return -1;
			if (!aIsAccepted && bIsAccepted) return 1;

			return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		});
	}, [pairs]);

	const getApprovedText = (status: string) => {
		switch (status) {
			case 'accepted':
				return t('programPanel.application.statusOptions.accepted');
			case 'rejected':
				return t('programPanel.application.statusOptions.rejected');
			case 'not accepted':
			default:
				return t('programPanel.application.statusOptions.notAccepted');
		}
	};

	// Actions hook
	const actions = useApplicationActions(
		application,
		program,
		{
			handleAccept,
			setRejectConfirmDialogOpen,
			setEditApplicationDialogOpen,
			handleCancelDeleting,
			setRestoreConfirmDialogOpen,
			setRemoveApplicationDialogOpen,
			setTransferDialogVisible,
		},
		{
			sending,
			restoringApplication,
			finalDeleteApplicationLoading,
			sendEditSuccess,
		}
	);

	const rejectApplicationDialogActions = [
		{
			key: 'reject',
			submit: true,
			label: t('actions.reject'),
			onClick: () => {
				trigger('reason').then((valid) => {
					if (valid) {
						handleReject(getValues('reason'));
					}
				});
			},
			loading: sending,
		},
		{
			key: 'reject-without-notification',
			label: t('actions.rejectWithoutNotification'),
			onClick: () => {
				handleReject(getValues('reason') || '', false);
			},
			loading: sending,
			variant: 'primary-text' as const,
		},
	];

	if (applicationLoading) {
		return (
			<div className="flex justify-content-center">
				<Spinner />
			</div>
		);
	}

	if (applicationError) {
		return <PageMissing />;
	}

	if (!application) {
		return null;
	}

	return (
		<>
			<Helmet title={t('organizationPanel.programPairingTests')} />

			<SubPageTitle
				title={
					application?.applicationRole === 'mentor'
						? t('programPanel.application.titleForMentor')
						: t('programPanel.application.titleForMentee')
				}
				additional={program?.displayName}
			/>

			<DialogConfirm
				message={t('programPanel.application.confirmRemovePairProposition')}
				visible={removePairDialogOpen}
				onHide={() => setRemovePairDialogOpen(false)}
				onConfirm={handleRemovePair}
				confirmAction={{ loading: deletePairLoading }}
			/>

			<DialogConfirm
				message={t('programPanel.application.confirmCreatePair')}
				visible={acceptPairDialogOpen}
				onHide={() => setAcceptPairDialogOpen(false)}
				onConfirm={handleAcceptPair}
				confirmAction={{ loading: acceptPairLoading }}
			/>

			<DialogConfirm
				message={t('programPanel.application.confirmRestoreApplication')}
				visible={restoreConfirmDialogOpen}
				onHide={() => setRestoreConfirmDialogOpen(false)}
				onConfirm={handleRestoreApplication}
				confirmAction={{ loading: restoringApplication }}
			/>

			<DeleteApplicationConfirmDialog
				control={deleteApplicationControl}
				onConfirm={handleFormSubmit}
				visible={removeApplicationDialogOpen}
				onHide={() => setRemoveApplicationDialogOpen(false)}
				confirmAction={{ loading: deleteApplicationLoading }}
			/>

			<EditApplicationDialog
				visible={editApplicationDialogOpen}
				onHide={() => setEditApplicationDialogOpen(false)}
				applicationData={application}
				programMembershipId={application?.programMembership.id}
				applicationRole={application.applicationRole}
				editMode
				programId={application.programId}
				onApplicationAdded={refetchApplication}
				onCancel={() => {}}
			/>
			<TransferApplicationDialog
				visible={transferDialogVisible}
				onHide={() => setTransferDialogVisible(false)}
				application={application}
				programId={Number(program?.id)}
				onTransferSuccess={() => {
					refetchApplication();
				}}
			/>
			<DialogAction
				title={t('programPanel.application.enterRejectReason')}
				visible={rejectConfirmDialogOpen}
				onHide={() => setRejectConfirmDialogOpen(false)}
				actions={rejectApplicationDialogActions}
			>
				<ValidateTextArea
					name="reason"
					control={control}
					required={rejectWithNotification}
					label={t('programPanel.application.rejectReasonOptional')}
				/>
			</DialogAction>

			<div className="flex flex-row justify-content-between align-items-center">
				<h2>{t('programPanel.application.pairTest')}</h2>
				{application?.limitStats && (
					<div>
						{`${t('programPanel.application.pairPropositions.acceptedPairs')}: `}
						<span
							className={classNames({
								'text-success font-bold': application.approved === 'accepted',
								'text-red-600 font-bold': application.approved === null,
							})}
						>
							{application.limitStats.currentPairs}/{application.pairLimit}
						</span>
					</div>
				)}
			</div>

			<Box variant="white-bordered">
				<BoxSection>
					<BusinessCard
						configPanel
						data={application}
						highlightDeleted
						displayHidden
						renderBottom={() => {
							const hasLongRejectReason =
								application.rejectReason && application.rejectReason.length > 100;

							return (
								<>
									<div
										className={`flex gap-2 justify-content-between ${
											hasLongRejectReason ? 'flex-column' : 'flex-column lg:flex-row'
										}`}
									>
										<span className="min-w-4rem">
											{t('programPanel.application.applicationHasStatus', {
												status: application.finished
													? t('programPanel.application.statusOptions.finished')
													: getApprovedText(application.approved),
											})}
											<ExpandableRejectReason reason={application.rejectReason || ''} />
										</span>
										<div
											className={`flex flex-row flex-wrap gap-2 ${
												hasLongRejectReason
													? 'justify-content-start mt-3'
													: 'justify-content-end'
											}`}
										>
											{actions.map((action) => (
												<ActionButton key={action.id} action={action} />
											))}
										</div>
									</div>

									{renderChangelogSection()}
								</>
							);
						}}
					/>
				</BoxSection>
			</Box>

			<div>
				<div className="flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
					<div className="flex-1">
						<h2>{t('programPanel.application.pairPropositions.title')}</h2>
						{application?.applicationRole === 'mentor' && (
							<p>{t('programPanel.application.pairPropositions.descMentor')}</p>
						)}
						{application?.applicationRole === 'mentee' && (
							<p>{t('programPanel.application.pairPropositions.descMentee')}</p>
						)}
						{!program?.pairingStatus && (
							<Message
								severity="info"
								text={t('programPanel.application.pairPropositions.pairingDisabledInfo')}
								className="w-full justify-content-start pl-4 mb-4"
							/>
						)}
					</div>
					<Button
						onClick={() => {
							setManualMode(false);
							refetchPairs();
						}}
						label={t('actions.refresh')}
						loading={pairsLoading || isRefetching}
					/>
				</div>

				{pairs?.length === 0 && (
					<p className="text-gray">
						{application?.approved === 'not accepted'
							? t('programPanel.application.pairPropositions.noPropositionsApplicationNotAccepted')
							: t('programPanel.application.pairPropositions.noPropositionsApplicationAccepted')}
					</p>
				)}

				{(pairs || []).length > 0 && (
					<ul className="p-list-unstylled">
						{sortedPairs.map((pair) => {
							const isAccepted = pair.acceptedByMentee !== null && pair.acceptedByMentor !== null;
							const isRejected = pair.status === 'rejected';
							const targetHasLimit =
								application?.applicationRole === 'mentee'
									? pair.mentorApplication?.limitStats?.pairLimitReached
									: pair.menteeApplication?.limitStats?.pairLimitReached;

							const shouldShowRedBorder = isRejected || (!isAccepted && targetHasLimit);
							const hasContract = Boolean(pair.contractId);
							return (
								<li key={pair.id} className="mb-4">
									<div className="rounded-lg overflow-hidden">
										<PairItem
											id={`pair-${pair.id}`}
											withGreenBorder={isAccepted}
											withRedBorder={shouldShowRedBorder}
											data={pair}
											userAreas={userAreas}
											rednerBottom={() => {
												return (
													<>
														<hr className="my-3" />
														<div className="flex justify-content-between align-items-center flex-wrap gap-2">
															<div>
																<div>
																	<div>
																		{getPairStatusInfo(pair)},{' '}
																		<Link
																			to={`/program/${program?.name}/pair/${pair.id}`}
																			className="text-purplish-blue"
																		>
																			{t('programPanel.application.pairDetails')}
																		</Link>
																		.
																	</div>
																	<PairActivityTimestamps
																		pair={{
																			createdAt: pair.createdAt,
																			entryMeeting: pair.entryMeeting,
																			acceptedByMentee: pair.acceptedByMentee,
																			acceptedByMentor: pair.acceptedByMentor,
																			rejectedByMentee: pair.rejectedByMentee,
																			rejectedByMentor: pair.rejectedByMentor,
																		}}
																	/>
																	{(() => {
																		const scoringData = getPairScoringText(pair);
																		if (!scoringData) return null;

																		return (
																			<div className="mt-1">
																				{scoringData.label}:{' '}
																				<span className="text-purplish-blue font-bold">
																					{scoringData.points.toFixed(0)}%
																				</span>
																				{pair.scoring_comment && (
																					<> ({pair.scoring_comment})</>
																				)}
																			</div>
																		);
																	})()}
																</div>
															</div>
															<div className="flex gap-2">
																<Tooltip
																	target={`#remove-button-${pair.id}`}
																	position="top"
																/>
																<span
																	id={`remove-button-${pair.id}`}
																	data-pr-tooltip={
																		hasContract
																			? t(
																					'programPanel.application.pairPropositions.cantRemovePairProposition'
																			  )
																			: undefined
																	}
																>
																	<Button
																		onClick={() => {
																			setCurrentPair(pair.id);
																			setRemovePairDialogOpen(true);
																		}}
																		label={t(
																			'programPanel.application.pairPropositions.removePairProposition'
																		)}
																		variant="danger"
																		disabled={hasContract}
																		loading={deletePairLoading}
																	/>
																</span>

																{!isAccepted && (
																	<>
																		<Tooltip
																			target={`#accept-button-${pair.id}`}
																			position="top"
																		/>
																		<span
																			id={`accept-button-${pair.id}`}
																			data-pr-tooltip={(function () {
																				if (
																					application.approved !== 'accepted'
																				) {
																					return t(
																						'programPanel.application.pairPropositions.applicationNotAccepted'
																					);
																				}
																				if (
																					currentApplicationPairLimitReached
																				) {
																					return t(
																						'programPanel.application.pairPropositions.currentApplicationPairLimitReached'
																					);
																				}
																				if (targetHasLimit) {
																					return t(
																						'programPanel.application.pairPropositions.limitReached'
																					);
																				}
																				if (isAccepted) {
																					return t(
																						'programPanel.application.pairPropositions.accepted'
																					);
																				}
																				if (pair.entryMeeting) {
																					return t(
																						'programPanel.application.pairPropositions.entryMeeting'
																					);
																				}
																				return undefined;
																			})()}
																		>
																			<Button
																				onClick={() => {
																					setCurrentPair(pair.id);
																					setAcceptPairDialogOpen(true);
																				}}
																				label={t(
																					'programPanel.application.pairPropositions.acceptPair'
																				)}
																				disabled={
																					application.approved !==
																						'accepted' ||
																					currentApplicationPairLimitReached ||
																					isRejected ||
																					targetHasLimit
																				}
																				loading={acceptPairLoading}
																			/>
																		</span>
																	</>
																)}
															</div>
														</div>
													</>
												);
											}}
										/>
									</div>
								</li>
							);
						})}
					</ul>
				)}

				<div className="mt-6">
					<div className="flex-1">
						<h2>{t('programPanel.application.pairPropositions.addNewPropositionTitle')}</h2>
						<p>{t('programPanel.application.pairPropositions.addNewPropositionDescription')}</p>
					</div>
					<div className="flex gap-2">
						<Dropdown
							placeholder={t('programPanel.application.pairPropositions.addPairPropositionPlaceholder')}
							className="flex-1"
							value={applicationToAdd}
							onChange={({ value }) => {
								setApplicationToAdd(value);
							}}
							dataKey="id"
							optionValue="id"
							options={propositions
								.sort((a, b) =>
									String(a.programMembership.user.lastName || '').localeCompare(
										String(b.programMembership.user.lastName || ''),
										'en',
										{ sensitivity: 'base' }
									)
								)
								.map((proposition: Application) => {
									const displayName = `${proposition.programMembership.user.lastName} ${proposition.programMembership.user.firstName}`;
									const additionalLabel = proposition.limitStats
										? `(${t(
												'programPanel.application.pairPropositions.acceptedPairs'
										  )}: ${`${proposition.limitStats.currentPairs}/${proposition.pairLimit}`})`
										: null;
									return {
										id: proposition.id,
										label: displayName,
										additionalLabel,
										disabled: currentPairsApplicationIdentifiers.includes(proposition.id),
										className: proposition.deletedAt ? 'text-error' : undefined,
									};
								})}
							filter
							itemTemplate={(item) => (
								<div>
									{item.label}{' '}
									{item.additionalLabel && <span className="text-gray">{item.additionalLabel}</span>}
								</div>
							)}
						/>
						<Button
							onClick={handleAddPair}
							label={t('actions.add')}
							disabled={!applicationToAdd}
							loading={isAddingPair}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default ProgramPairingDetails;
